// src/components/common/BroadcastFeedbackPage.tsx
// Public page — no auth required.
// Reads broadcastId from URL param and r + uid from search params,
// then fires a GET to record feedback and shows a confirmation card.

import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import BackgroundShapes from './BackgroundShapes';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

type PageState = 'loading' | 'success' | 'error';

const BroadcastFeedbackPage: React.FC = () => {
  const { broadcastId } = useParams<{ broadcastId: string }>();
  const [searchParams] = useSearchParams();
  const r = searchParams.get('r');
  const uid = searchParams.get('uid');

  const [state, setState] = useState<PageState>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    // Local validation before hitting the API
    if (!r || !uid) {
      setErrorMessage('This feedback link is incomplete. Please use the link from your email.');
      setState('error');
      return;
    }

    const recordFeedback = async () => {
      try {
        const url = `${API_BASE}/broadcasts/${broadcastId}/feedback?r=${encodeURIComponent(r)}&uid=${encodeURIComponent(uid)}`;
        const response = await fetch(url);
        const data = await response.json();

        if (!response.ok || !data.success) {
          setErrorMessage(data.message || 'Something went wrong. Please try again.');
          setState('error');
          return;
        }

        setState('success');
      } catch {
        setErrorMessage('Unable to connect. Please check your connection and try again.');
        setState('error');
      }
    };

    recordFeedback();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Fire once on mount — params are stable

  return (
    <div className="min-h-screen bg-paper relative flex items-center justify-center px-4 py-16">
      <BackgroundShapes />

      <div className="relative z-10 w-full max-w-md">
        {/* ShiYou wordmark */}
        <div className="text-center mb-8">
          <span
            className="text-3xl font-bold"
            style={{ color: '#E8A066' }}
          >
            ShiYou
          </span>
          <p className="text-coffee opacity-70 text-sm mt-1">
            NYC &amp; Jersey City Roommate Matching
          </p>
        </div>

        {/* Card */}
        <div className="bg-cream border-2 border-coffee rounded-xl p-8 shadow-[4px_4px_0px_#594A42] text-center">
          {state === 'loading' && (
            <>
              <div
                className="inline-block w-8 h-8 border-4 border-terracotta border-t-transparent rounded-full animate-spin mb-4"
                role="status"
                aria-label="Loading"
              />
              <p className="text-coffee font-medium">Recording your response&hellip;</p>
            </>
          )}

          {state === 'success' && (
            <>
              <div className="text-4xl mb-4">🎉</div>
              <h1 className="text-2xl font-bold text-coffee mb-2">
                Thanks for your feedback!
              </h1>
              <p className="text-coffee opacity-80 text-sm">
                Your response has been recorded. We appreciate you taking the time.
              </p>
            </>
          )}

          {state === 'error' && (
            <>
              <div className="text-4xl mb-4">😕</div>
              <h1 className="text-2xl font-bold text-coffee mb-2">
                Something went wrong
              </h1>
              <p className="text-coffee opacity-80 text-sm">
                {errorMessage}
              </p>
            </>
          )}
        </div>

        <p className="text-center text-xs text-coffee opacity-40 mt-6">
          ShiYou — Find your perfect roommate in NYC &amp; Jersey City
        </p>
      </div>
    </div>
  );
};

export default BroadcastFeedbackPage;
